(() => {
  const unicodeFractions = {
    '¼': 0.25,
    '½': 0.5,
    '¾': 0.75,
    '⅐': 1 / 7,
    '⅑': 1 / 9,
    '⅒': 0.1,
    '⅓': 1 / 3,
    '⅔': 2 / 3,
    '⅕': 0.2,
    '⅖': 0.4,
    '⅗': 0.6,
    '⅘': 0.8,
    '⅙': 1 / 6,
    '⅚': 5 / 6,
    '⅛': 0.125,
    '⅜': 0.375,
    '⅝': 0.625,
    '⅞': 0.875
  };

  const numberPattern = String.raw`(?:\d+\s+\d+\/\d+|\d+[.,]\d+|\d+\/\d+|\d+|[\u00bc-\u00be\u2150-\u215e])`;
  const amountPattern = String.raw`(${numberPattern})(?:\s*(bis|[-\u2013\u2014])\s*(${numberPattern}))?`;
  const knownUnits = [
    'Essl\u00f6ffel', 'Teel\u00f6ffel', 'Kilogramm', 'Gramm', 'Milligramm', 'Milliliter', 'Zentiliter', 'Deziliter',
    'Liter', 'St\u00fcck', 'Stk\\.?', 'Prisen?', 'Bunde?', 'Dosen?', 'Gl(?:as|\u00e4ser)', 'Flaschen?', 'Tassen?',
    'Packungen?', 'Scheiben?', 'Zehen?', 'Zweige?', 'Bl(?:att|\u00e4tter)', 'Handvoll', 'K\u00f6pfe?', 'Kopf',
    'EL', 'TL', 'kg', 'mg', 'ml', 'cl', 'dl', 'g', 'l'
  ];
  const unitPattern = knownUnits.join('|');
  const ingredientPattern = new RegExp(String.raw`^\s*${amountPattern}(?:\s+(${unitPattern})(?=\s|\||$))?\s*`, 'iu');

  function parseNumber(value) {
    const normalized = value.trim();

    if (unicodeFractions[normalized] !== undefined) {
      return unicodeFractions[normalized];
    }

    if (/^\d+\s+\d+\/\d+$/.test(normalized)) {
      const [whole, fraction] = normalized.split(/\s+/);
      const [numerator, denominator] = fraction.split('/').map(Number);
      return Number(whole) + numerator / denominator;
    }

    if (/^\d+\/\d+$/.test(normalized)) {
      const [numerator, denominator] = normalized.split('/').map(Number);
      return numerator / denominator;
    }

    return Number(normalized.replace(',', '.'));
  }

  function parseIngredient(text) {
    const delimiterIndex = text.indexOf('|');
    const prefix = delimiterIndex >= 0 ? text.slice(0, delimiterIndex) : text;
    const match = prefix.match(ingredientPattern);

    if (!match) {
      return null;
    }

    const consumedLength = delimiterIndex >= 0 ? delimiterIndex + 1 : match[0].length;

    return {
      start: parseNumber(match[1]),
      separator: match[2] || '',
      end: match[3] ? parseNumber(match[3]) : null,
      unit: match[4] || '',
      consumedLength
    };
  }

  function trimTextPrefix(element, charactersToRemove) {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let currentNode;

    while ((currentNode = walker.nextNode())) {
      textNodes.push(currentNode);
    }

    let remaining = charactersToRemove;

    for (const textNode of textNodes) {
      if (remaining <= 0) break;

      if (remaining >= textNode.data.length) {
        remaining -= textNode.data.length;
        textNode.data = '';
      } else {
        textNode.data = textNode.data.slice(remaining);
        remaining = 0;
      }
    }

    const firstTextNode = textNodes.find((textNode) => textNode.data.length > 0);
    if (firstTextNode) {
      firstTextNode.data = firstTextNode.data.replace(/^\s+/, '');
    }

    element.querySelectorAll('*').forEach((child) => {
      if (!child.textContent.trim() && !child.querySelector('img, svg, video')) {
        child.remove();
      }
    });
  }

  function formatNumber(value) {
    const rounded = Math.round(value * 1000) / 1000;
    const whole = Math.floor(rounded);
    const fraction = rounded - whole;
    const fractionMap = [
      [0.125, '⅛'],
      [0.25, '¼'],
      [1 / 3, '⅓'],
      [0.375, '⅜'],
      [0.5, '½'],
      [0.625, '⅝'],
      [2 / 3, '⅔'],
      [0.75, '¾'],
      [0.875, '⅞']
    ];
    const matchingFraction = fractionMap.find(([candidate]) => Math.abs(fraction - candidate) < 0.006);

    if (matchingFraction) {
      return `${whole || ''}${matchingFraction[1]}`;
    }

    return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(rounded);
  }

  function getIngredientRows(source) {
    const richText = source.querySelector('.metafield-rich_text_field') || source;
    const rows = [];

    for (const child of richText.children) {
      if (child.matches('ul, ol')) {
        rows.push(...child.querySelectorAll(':scope > li'));
      } else if (child.matches('p')) {
        rows.push(child);
      }
    }

    return rows.filter((row) => row.textContent.trim());
  }

  class RecipeIngredients extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.source = this.querySelector('[data-recipe-ingredients-source]');
      this.table = this.querySelector('[data-recipe-ingredients-table]');
      this.portionsInput = this.querySelector('[data-portions-input]');
      this.decreaseButton = this.querySelector('[data-portions-decrease]');
      this.increaseButton = this.querySelector('[data-portions-increase]');
      this.basePortions = Number(this.dataset.basePortions) || 1;
      this.rows = [];

      if (!this.source || !this.table || !this.portionsInput) return;

      const sourceRows = getIngredientRows(this.source);
      if (sourceRows.length === 0) return;

      // bb: recipes without any parsable amounts keep the plain ingredient list.
      // Scaling portions would have nothing to scale, so the calculator stays hidden.
      const hasScalableAmounts = sourceRows.some((sourceRow) => parseIngredient(sourceRow.textContent) !== null);
      if (!hasScalableAmounts) return;

      sourceRows.forEach((sourceRow) => this.addRow(sourceRow));
      this.bindEvents();
      this.update(this.basePortions);
      this.classList.add('is-enhanced');
      this.initialized = true;
    }

    addRow(sourceRow) {
      const parsed = parseIngredient(sourceRow.textContent);
      const row = document.createElement('div');
      const amount = document.createElement('span');
      const unit = document.createElement('span');
      const ingredient = document.createElement('div');
      const ingredientContent = sourceRow.cloneNode(true);

      row.className = 'recipe-ingredients__row';
      row.setAttribute('role', 'listitem');
      amount.className = 'recipe-ingredients__amount';
      unit.className = 'recipe-ingredients__unit';
      ingredient.className = 'recipe-ingredients__name';

      if (parsed) {
        trimTextPrefix(ingredientContent, parsed.consumedLength);
        unit.textContent = parsed.unit;
      }

      ingredient.append(ingredientContent);
      row.append(amount, unit, ingredient);
      this.table.append(row);
      this.rows.push({ amount, parsed });
    }

    bindEvents() {
      this.portionsInput.addEventListener('input', () => {
        const portions = Number(this.portionsInput.value);
        if (Number.isFinite(portions) && portions >= 1) {
          this.update(portions);
        }
      });

      this.portionsInput.addEventListener('change', () => {
        const portions = Math.min(99, Math.max(1, Math.round(Number(this.portionsInput.value) || this.basePortions)));
        this.portionsInput.value = String(portions);
        this.update(portions);
      });

      this.decreaseButton?.addEventListener('click', () => this.step(-1));
      this.increaseButton?.addEventListener('click', () => this.step(1));
    }

    step(direction) {
      const current = Number(this.portionsInput.value) || this.basePortions;
      const next = Math.min(99, Math.max(1, Math.round(current + direction)));
      this.portionsInput.value = String(next);
      this.update(next);
    }

    update(portions) {
      const factor = portions / this.basePortions;

      this.rows.forEach(({ amount, parsed }) => {
        if (!parsed) {
          amount.textContent = '';
          return;
        }

        const start = formatNumber(parsed.start * factor);
        if (parsed.end === null) {
          amount.textContent = start;
          return;
        }

        const separator = parsed.separator.toLocaleLowerCase('de-DE') === 'bis' ? ' bis ' : '\u2013';
        amount.textContent = `${start}${separator}${formatNumber(parsed.end * factor)}`;
      });

      if (this.decreaseButton) {
        this.decreaseButton.disabled = portions <= 1;
      }

      if (this.increaseButton) {
        this.increaseButton.disabled = portions >= 99;
      }
    }
  }

  class RecipeContentTabs extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.tabs = [...this.querySelectorAll('[data-recipe-tab]')];
      this.panels = [...this.querySelectorAll('[data-recipe-tab-panel]')];

      if (this.tabs.length < 2 || this.tabs.length !== this.panels.length) return;

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.activate(index));
        tab.addEventListener('keydown', (event) => this.handleKeydown(event, index));
      });

      this.activate(0);
      this.classList.add('is-enhanced');
      this.initialized = true;
    }

    activate(activeIndex, moveFocus = false) {
      this.tabs.forEach((tab, index) => {
        const isActive = index === activeIndex;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
        this.panels[index].classList.toggle('is-active', isActive);
      });

      if (moveFocus) this.tabs[activeIndex].focus();
    }

    handleKeydown(event, currentIndex) {
      let nextIndex = null;

      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;

      if (nextIndex === null) return;

      event.preventDefault();
      this.activate(nextIndex, true);
    }
  }

  if (!customElements.get('recipe-ingredients')) {
    customElements.define('recipe-ingredients', RecipeIngredients);
  }

  if (!customElements.get('recipe-content-tabs')) {
    customElements.define('recipe-content-tabs', RecipeContentTabs);
  }
})();
